import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Paper, Typography, Box, Button, Alert,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, LinearProgress, Divider, Collapse
} from '@mui/material';
import { EventNote, Settings, Refresh, Add, Delete, NotificationsActive, ExpandMore, ExpandLess, VisibilityOff, Visibility, CurrencyPound, Numbers } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { manufacturingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const formatDateParam = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const todayStr = () => formatDateParam(new Date());

/** Build table rows: overdue header, month headers, week headers (Week 1-5), weekday date rows only. */
const buildTableRows = (dates) => {
  if (!dates || dates.length === 0) return [];

  const today = todayStr();
  const rows = [];
  let currentMonth = null;
  let weekCounter = 0;
  let lastMondayKey = null;
  let overdueHeaderAdded = false;
  let pastOverdueSection = false;

  for (const dateStr of dates) {
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay();

    // Skip weekends
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    const isOverdue = dateStr < today;

    if (isOverdue) {
      // Add overdue section header once
      if (!overdueHeaderAdded) {
        overdueHeaderAdded = true;
        rows.push({
          type: 'overdue-header',
          label: 'Overdue',
          key: 'overdue-header',
        });
      }

      rows.push({
        type: 'date',
        dateStr,
        isToday: false,
        isOverdue: true,
        displayDate: `${DAY_NAMES[dayOfWeek]} ${date.getDate()}/${date.getMonth() + 1}`,
        key: dateStr,
      });
      continue;
    }

    // Transitioning out of overdue section — reset month/week tracking
    if (overdueHeaderAdded && !pastOverdueSection) {
      pastOverdueSection = true;
      currentMonth = null;
      weekCounter = 0;
      lastMondayKey = null;
    }

    const month = date.getMonth();
    const year = date.getFullYear();
    const monthKey = `${year}-${month}`;

    // Month header — reset week counter
    if (monthKey !== currentMonth) {
      currentMonth = monthKey;
      weekCounter = 0;
      lastMondayKey = null;
      rows.push({
        type: 'month-header',
        label: `${MONTH_NAMES[month]} ${year}`,
        key: `month-${monthKey}`,
      });
    }

    // Find the Monday of this date's week
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(date);
    monday.setDate(date.getDate() + mondayOffset);
    const mondayKey = formatDateParam(monday);

    // New week when Monday changes
    if (mondayKey !== lastMondayKey) {
      lastMondayKey = mondayKey;
      weekCounter++;
      rows.push({
        type: 'week-header',
        label: `Week ${weekCounter}`,
        key: `week-${monthKey}-w${weekCounter}`,
      });
    }

    // Date row
    rows.push({
      type: 'date',
      dateStr,
      isToday: dateStr === today,
      isOverdue: false,
      displayDate: `${DAY_NAMES[dayOfWeek]} ${date.getDate()}/${date.getMonth() + 1}`,
      key: dateStr,
    });
  }

  return rows;
};

const CHIP_COLORS = {
  allocated: '#ffa726',   // orange
  shipped: '#4caf50',     // green
  outstanding: '#ef5350', // red
};

/** Compact money format for dense grid cells: £13.3k, £1.2M, £133. */
const fmtMoney = (n) => {
  const v = Math.round(n);
  const abs = Math.abs(v);
  if (abs >= 1000000) return `£${(v / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `£${(v / 1000).toFixed(1)}k`;
  return `£${v.toLocaleString()}`;
};

/** Format a value for display: quantities as plain rounded numbers, monetary values abbreviated with a £ prefix. */
const fmtCell = (n, currency) => (currency ? fmtMoney(n) : Math.round(n).toLocaleString());

/** Render tree-style cell: ordered on top, then allocated (orange), shipped (green), outstanding (red). */
const CellValues = ({ ordered, allocated, shipped, outstanding, inheritColor, hideZeros, hideAllocations, emphasis, currency, onClick }) => {
  const ord = Math.round(ordered);
  const alloc = Math.round(allocated);
  const ship = Math.round(shipped);
  const out = Math.round(outstanding);
  const allZero = ord === 0 && alloc === 0 && ship === 0 && out === 0;
  if (allZero && hideZeros) return null;
  const chipSx = (bg) => ({
    fontSize: emphasis ? '0.75rem' : '0.7rem', fontWeight: 700,
    color: '#fff',
    backgroundColor: bg,
    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
    borderRadius: '3px', px: 0.5,
  });
  return (
    <Box
      onClick={!allZero && onClick ? onClick : undefined}
      sx={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: emphasis ? 56 : 48,
        ...(!allZero && onClick && {
          cursor: 'pointer',
          borderRadius: '4px',
          '&:hover': { outline: '2px solid rgba(25, 118, 210, 0.5)', outlineOffset: '1px' },
        }),
      }}
      title={
        currency && !allZero
          ? `Ordered £${ord.toLocaleString()} · Alloc £${alloc.toLocaleString()} · Shipped £${ship.toLocaleString()} · Outstanding £${out.toLocaleString()}${onClick ? ' — click for breakdown' : ''}`
          : (!allZero && onClick ? 'Click to see order breakdown' : undefined)
      }
    >
      <Box sx={{ fontWeight: emphasis ? 900 : 700, fontSize: emphasis ? '1.1rem' : '0.85rem', ...(emphasis && { textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }) }}>{fmtCell(ord, currency)}</Box>
      {!allZero && !hideAllocations && <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 0.5 }}>
        <Box sx={chipSx(CHIP_COLORS.allocated)}>{fmtCell(alloc, currency)}</Box>
        <Box sx={chipSx(CHIP_COLORS.shipped)}>{fmtCell(ship, currency)}</Box>
        <Box sx={chipSx(CHIP_COLORS.outstanding)}>{fmtCell(out, currency)}</Box>
      </Box>}
    </Box>
  );
};

/** Return background colour for a daily total based on alert thresholds. */
const getTotalAlertColor = (value, thresholds) => {
  if (!thresholds || thresholds.red == null || thresholds.orange == null) return undefined;
  const red = Number(thresholds.red);
  const orange = Number(thresholds.orange);
  if (value >= red) return '#ef5350';   // red
  if (value >= orange) return '#ffa726'; // orange
  return '#66bb6a';                      // green
};

// --- Legend ---
const LegendChip = ({ color, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{
      backgroundColor: color, color: '#fff', fontWeight: 700, fontSize: '0.7rem',
      textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
      borderRadius: '3px', px: 0.75, py: 0.1,
    }}>
      123
    </Box>
    <Typography variant="body2">{label}</Typography>
  </Box>
);

const CapacityLegend = ({ showValues }) => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
    {showValues && (
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
        Showing order value (£)
      </Typography>
    )}
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{showValues ? '£123' : '123'}</Typography>
      <Typography variant="body2">{showValues ? 'Total value ordered (due that day)' : 'Total ordered (due that day)'}</Typography>
    </Box>
    <LegendChip color={CHIP_COLORS.allocated} label="Allocated — stock reserved, not yet shipped" />
    <LegendChip color={CHIP_COLORS.shipped} label="Shipped — already dispatched" />
    <LegendChip color={CHIP_COLORS.outstanding} label="Outstanding — not allocated or shipped" />
    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
      Click any number to see the orders behind it
    </Typography>
  </Paper>
);

// --- Cell Details Dialog ---
function CellDetailsDialog({ info, currency, onClose }) {
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!info) return;
    setLoading(true);
    setLines([]);
    manufacturingAPI.capacityPlanning.getDetails({
      date: info.date,
      columns: JSON.stringify(info.columns),
    })
      .then(res => {
        // Outstanding orders first (largest outstanding at the top), then by order number
        const sorted = [...(res.data.lines || [])].sort((a, b) => {
          const aHasOut = a.outstanding > 0 ? 1 : 0;
          const bHasOut = b.outstanding > 0 ? 1 : 0;
          if (aHasOut !== bHasOut) return bHasOut - aHasOut;
          if (a.outstanding !== b.outstanding) return b.outstanding - a.outstanding;
          return (a.order_number || '').localeCompare(b.order_number || '');
        });
        setLines(sorted);
      })
      .catch(() => toast.error('Failed to load order details'))
      .finally(() => setLoading(false));
  }, [info]);

  // In value mode, read the monetary fields and render with a £ prefix.
  const fld = (l, qtyKey, valKey) => (currency ? (l[valKey] || 0) : (l[qtyKey] || 0));
  const fmt = (n) => (currency ? `£${Math.round(n).toLocaleString()}` : Math.round(n).toLocaleString());

  const numCell = (value, color) => (
    <TableCell align="right" sx={{ fontWeight: 600, px: 1, color: Math.round(value) > 0 ? color : 'text.disabled' }}>
      {fmt(value)}
    </TableCell>
  );

  const totals = lines.reduce((acc, l) => ({
    ordered: acc.ordered + fld(l, 'quantity_ordered', 'value'),
    allocated: acc.allocated + fld(l, 'allocated', 'value_allocated'),
    shipped: acc.shipped + fld(l, 'shipped', 'value_shipped'),
    outstanding: acc.outstanding + fld(l, 'outstanding', 'value_outstanding'),
  }), { ordered: 0, allocated: 0, shipped: 0, outstanding: 0 });

  return (
    <Dialog open={!!info} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {info?.title}
        <Typography variant="body2" color="text.secondary">{info?.displayDate}</Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {!loading && lines.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>No order lines found for this date.</Typography>
        )}
        {lines.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& th, & td': { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Order #</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ordered{currency ? ' (£)' : ''}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: CHIP_COLORS.allocated }}>Allocated{currency ? ' (£)' : ''}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: CHIP_COLORS.shipped }}>Shipped{currency ? ' (£)' : ''}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: CHIP_COLORS.outstanding }}>Outstanding{currency ? ' (£)' : ''}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ width: 44 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{l.order_number}</TableCell>
                  <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.customer_name}>{l.customer_name}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.product_code}</TableCell>
                  <TableCell align="right">{fmt(fld(l, 'quantity_ordered', 'value'))}</TableCell>
                  {numCell(fld(l, 'allocated', 'value_allocated'), CHIP_COLORS.allocated)}
                  {numCell(fld(l, 'shipped', 'value_shipped'), CHIP_COLORS.shipped)}
                  {numCell(fld(l, 'outstanding', 'value_outstanding'), CHIP_COLORS.outstanding)}
                  <TableCell sx={{ whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                    {(l.order_status || '').replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    {l.order_id && (
                      <Tooltip title={`View order ${l.order_number}`}>
                        <IconButton
                          size="small"
                          color="primary"
                          component={RouterLink}
                          to={`/manufacturing/customer-orders/${l.order_id}`}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { borderTop: '2px solid rgba(0,0,0,0.2)', fontWeight: 700 } }}>
                <TableCell colSpan={3}>Total</TableCell>
                <TableCell align="right">{fmt(totals.ordered)}</TableCell>
                <TableCell align="right" sx={{ color: CHIP_COLORS.allocated }}>{fmt(totals.allocated)}</TableCell>
                <TableCell align="right" sx={{ color: CHIP_COLORS.shipped }}>{fmt(totals.shipped)}</TableCell>
                <TableCell align="right" sx={{ color: CHIP_COLORS.outstanding }}>{fmt(totals.outstanding)}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Tables Config Dialog ---
function TablesConfigDialog({ open, tables, onTablesChange, onSave, onClose }) {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (idx) => {
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleAddTable = () => {
    onTablesChange([...tables, { name: '', columns: [{ name: '', prefixes: [] }], alert_thresholds: {} }]);
    setExpanded(prev => ({ ...prev, [tables.length]: true }));
  };

  const handleRemoveTable = (index) => {
    onTablesChange(tables.filter((_, i) => i !== index));
  };

  const handleTableNameChange = (index, name) => {
    const updated = [...tables];
    updated[index] = { ...updated[index], name };
    onTablesChange(updated);
  };

  const handleAddColumn = (tableIdx) => {
    const updated = [...tables];
    updated[tableIdx] = {
      ...updated[tableIdx],
      columns: [...updated[tableIdx].columns, { name: '', prefixes: [] }],
    };
    onTablesChange(updated);
  };

  const handleRemoveColumn = (tableIdx, colIdx) => {
    const updated = [...tables];
    updated[tableIdx] = {
      ...updated[tableIdx],
      columns: updated[tableIdx].columns.filter((_, i) => i !== colIdx),
    };
    onTablesChange(updated);
  };

  const handleColumnNameChange = (tableIdx, colIdx, name) => {
    const updated = [...tables];
    const cols = [...updated[tableIdx].columns];
    cols[colIdx] = { ...cols[colIdx], name };
    updated[tableIdx] = { ...updated[tableIdx], columns: cols };
    onTablesChange(updated);
  };

  const handlePrefixesChange = (tableIdx, colIdx, prefixString) => {
    const prefixes = prefixString.split(',').map(p => p.trim()).filter(p => p.length > 0);
    const updated = [...tables];
    const cols = [...updated[tableIdx].columns];
    cols[colIdx] = { ...cols[colIdx], prefixes, _raw: prefixString };
    updated[tableIdx] = { ...updated[tableIdx], columns: cols };
    onTablesChange(updated);
  };

  const handleExclusionsChange = (tableIdx, colIdx, exclusionString) => {
    const exclusions = exclusionString.split(',').map(p => p.trim()).filter(p => p.length > 0);
    const updated = [...tables];
    const cols = [...updated[tableIdx].columns];
    cols[colIdx] = { ...cols[colIdx], exclusions, _rawExcl: exclusionString };
    updated[tableIdx] = { ...updated[tableIdx], columns: cols };
    onTablesChange(updated);
  };

  const handleAlertChange = (tableIdx, field, value) => {
    const updated = [...tables];
    updated[tableIdx] = {
      ...updated[tableIdx],
      alert_thresholds: { ...updated[tableIdx].alert_thresholds, [field]: value },
    };
    onTablesChange(updated);
  };

  const handleSave = () => {
    for (const t of tables) {
      if (!t.name.trim()) {
        toast.error('Each table must have a name');
        return;
      }
      if (t.columns.length === 0) {
        toast.error(`Table "${t.name}" must have at least one column`);
        return;
      }
      for (const c of t.columns) {
        if (!c.name.trim() || c.prefixes.length === 0) {
          toast.error(`All columns in "${t.name}" must have a name and at least one prefix`);
          return;
        }
      }
      // Validate alert thresholds if set
      const at = t.alert_thresholds;
      if (at && (at.red || at.orange)) {
        const r = Number(at.red);
        const o = Number(at.orange);
        if (at.red && at.orange && !isNaN(r) && !isNaN(o) && o >= r) {
          toast.error(`In "${t.name}", orange threshold must be less than red`);
          return;
        }
      }
    }
    // Clean _raw fields from columns
    const clean = tables.map(t => ({
      ...t,
      columns: t.columns.map(({ _raw, _rawExcl, ...rest }) => rest),
      alert_thresholds: cleanThresholds(t.alert_thresholds),
    }));
    onSave(clean);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure Capacity Tables</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each table appears as a column group. Configure columns and alert thresholds per table.
        </Typography>

        {tables.map((table, tIdx) => (
          <Paper key={tIdx} variant="outlined" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
              <IconButton size="small" onClick={() => toggleExpand(tIdx)}>
                {expanded[tIdx] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <TextField
                label="Table Name"
                value={table.name}
                onChange={(e) => handleTableNameChange(tIdx, e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                placeholder="e.g., Envelopes"
              />
              <IconButton color="error" onClick={() => handleRemoveTable(tIdx)}>
                <Delete />
              </IconButton>
            </Box>

            <Collapse in={expanded[tIdx]}>
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Columns</Typography>
                {table.columns.map((col, cIdx) => (
                  <Box key={cIdx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="Column Name"
                      value={col.name}
                      onChange={(e) => handleColumnNameChange(tIdx, cIdx, e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="e.g., Envelopes"
                    />
                    <TextField
                      label="Prefixes (comma-separated)"
                      value={col._raw !== undefined ? col._raw : col.prefixes.join(', ')}
                      onChange={(e) => handlePrefixesChange(tIdx, cIdx, e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="e.g., EN/, AB/"
                    />
                    <TextField
                      label="Exclusions (comma-separated)"
                      value={col._rawExcl !== undefined ? col._rawExcl : (col.exclusions || []).join(', ')}
                      onChange={(e) => handleExclusionsChange(tIdx, cIdx, e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="e.g., EN/SP"
                    />
                    <IconButton color="error" onClick={() => handleRemoveColumn(tIdx, cIdx)} size="small" sx={{ mt: 0.5 }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />} onClick={() => handleAddColumn(tIdx)} variant="text">
                  Add Column
                </Button>

                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  <NotificationsActive fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Alert Thresholds (Total column)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Red (at or above)"
                    value={table.alert_thresholds?.red ?? ''}
                    onChange={(e) => handleAlertChange(tIdx, 'red', e.target.value)}
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    InputProps={{ sx: { backgroundColor: '#ef535033' } }}
                  />
                  <TextField
                    label="Orange (at or above)"
                    value={table.alert_thresholds?.orange ?? ''}
                    onChange={(e) => handleAlertChange(tIdx, 'orange', e.target.value)}
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    InputProps={{ sx: { backgroundColor: '#ffa72633' } }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Leave blank to disable alerts for this table.
                </Typography>
              </Box>
            </Collapse>
          </Paper>
        ))}

        <Button startIcon={<Add />} onClick={handleAddTable} variant="outlined" fullWidth>
          Add Table
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={tables.length === 0}>
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Clean alert thresholds: return {red, orange} with numbers, or empty object. */
const cleanThresholds = (at) => {
  if (!at) return {};
  const r = at.red !== '' && at.red != null ? Number(at.red) : null;
  const o = at.orange !== '' && at.orange != null ? Number(at.orange) : null;
  if (r != null && o != null && !isNaN(r) && !isNaN(o)) return { red: r, orange: o };
  return {};
};

// --- Main Page ---
const CapacityPlanning = () => {
  usePageTitle('Capacity Planning');
  const { user } = useAuth();

  const [tables, setTables] = useState([]); // [{name, columns, alert_thresholds}, ...]
  const [tableData, setTableData] = useState([]); // API responses, one per table
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingTables, setEditingTables] = useState([]);
  const [detailInfo, setDetailInfo] = useState(null); // {date, displayDate, title, columns}
  const [hideZeros, setHideZeros] = useState(() => localStorage.getItem('capacity_hideZeros') === 'true');
  const [hideAllocations, setHideAllocations] = useState(() => localStorage.getItem('capacity_hideAllocations') === 'true');
  const [showValues, setShowValues] = useState(() => localStorage.getItem('capacity_showValues') === 'true');

  const toggleHideZeros = () => {
    setHideZeros(prev => {
      const next = !prev;
      localStorage.setItem('capacity_hideZeros', String(next));
      return next;
    });
  };

  const toggleHideAllocations = () => {
    setHideAllocations(prev => {
      const next = !prev;
      localStorage.setItem('capacity_hideAllocations', String(next));
      return next;
    });
  };

  const toggleShowValues = () => {
    setShowValues(prev => {
      const next = !prev;
      localStorage.setItem('capacity_showValues', String(next));
      return next;
    });
  };

  const isAdmin = user?.is_staff || user?.role === 'admin';
  // Order values are an admin-only view; ignore any stale saved preference for non-admins.
  const valueMode = isAdmin && showValues;

  // Load global config
  useEffect(() => {
    manufacturingAPI.capacityPlanning.getConfig().then(res => {
      const cfg = res.data;
      if (cfg.tables && cfg.tables.length > 0) {
        setTables(cfg.tables);
      }
    }).catch(() => {});
  }, []);

  // Count total columns across all tables (for colSpan on header rows)
  const totalColumns = useMemo(() => {
    // Date col + for each table: its columns + 1 Total col
    return 1 + tables.reduce((sum, t) => sum + (t.columns?.length || 0) + 1, 0);
  }, [tables]);

  const fetchData = useCallback(async () => {
    if (tables.length === 0) {
      setTableData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 84); // 12 weeks

      const promises = tables.map(t =>
        manufacturingAPI.capacityPlanning.getData({
          start_date: formatDateParam(start),
          end_date: formatDateParam(end),
          columns: JSON.stringify(t.columns.map(c => ({ name: c.name, prefixes: c.prefixes, exclusions: c.exclusions || [] }))),
        }).then(r => r.data)
      );
      const results = await Promise.all(promises);
      setTableData(results);
    } catch (err) {
      console.error('Failed to load capacity data:', err);
      setError('Failed to load capacity planning data');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tables]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (tables.length === 0) return;
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData, tables.length]);

  // Use dates from the first table's response (all are the same date range)
  const tableRows = useMemo(() => {
    const dates = tableData[0]?.dates;
    return buildTableRows(dates);
  }, [tableData]);

  // Open the order breakdown dialog for a cell. column = null means the table's Total cell.
  const openDetails = (row, table, column) => {
    const cols = column ? [column] : (table.columns || []);
    setDetailInfo({
      date: row.dateStr,
      displayDate: row.displayDate,
      title: column ? `${table.name} — ${column.name}` : `${table.name} — Total`,
      columns: cols.map(c => ({ name: c.name, prefixes: c.prefixes, exclusions: c.exclusions || [] })),
    });
  };

  const handleSaveTables = (newTables) => {
    setTables(newTables);
    setConfigDialogOpen(false);
    manufacturingAPI.capacityPlanning.updateConfig({ tables: newTables }).catch(() => {});
    toast.success('Configuration saved');
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600}>
          Capacity Planning
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => {
                setEditingTables(JSON.parse(JSON.stringify(tables)));
                setConfigDialogOpen(true);
              }}
            >
              Configure Tables
            </Button>
          )}
          <Tooltip title={hideZeros ? 'Show empty cells' : 'Hide empty cells'}>
            <Button
              variant={hideZeros ? 'contained' : 'outlined'}
              startIcon={hideZeros ? <VisibilityOff /> : <Visibility />}
              onClick={toggleHideZeros}
              size="small"
            >
              {hideZeros ? 'Zeros Hidden' : 'Zeros Shown'}
            </Button>
          </Tooltip>
          <Tooltip title={hideAllocations ? 'Show allocations' : 'Hide allocations'}>
            <Button
              variant={hideAllocations ? 'contained' : 'outlined'}
              startIcon={hideAllocations ? <VisibilityOff /> : <Visibility />}
              onClick={toggleHideAllocations}
              size="small"
            >
              {hideAllocations ? 'Allocations Hidden' : 'Allocations Shown'}
            </Button>
          </Tooltip>
          {isAdmin && (
            <Tooltip title={showValues ? 'Show quantities' : 'Show order values (£)'}>
              <Button
                variant={showValues ? 'contained' : 'outlined'}
                startIcon={showValues ? <CurrencyPound /> : <Numbers />}
                onClick={toggleShowValues}
                size="small"
              >
                {showValues ? 'Showing Value' : 'Showing Qty'}
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading || tables.length === 0}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tables.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EventNote sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No tables configured
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {isAdmin
              ? 'Configure capacity tables to see planning data.'
              : 'An admin needs to configure the capacity planning tables.'}
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Settings />}
              onClick={() => {
                setEditingTables([]);
                setConfigDialogOpen(true);
              }}
            >
              Configure Tables
            </Button>
          )}
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {tables.length > 0 && <CapacityLegend showValues={valueMode} />}

      {tableData.length > 0 && tables.length > 0 && (
        <Table size="small">
          <colgroup>
            <col style={{ width: 160 }} />
            {tables.map((t, tIdx) => (
              <React.Fragment key={tIdx}>
                {t.columns.map((_, cIdx) => (
                  <col key={`${tIdx}-${cIdx}`} />
                ))}
                <col style={{ width: 100 }} />
              </React.Fragment>
            ))}
          </colgroup>
          <TableHead>
            {/* Row 1: Table group names */}
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{ fontWeight: 'bold', verticalAlign: 'bottom', borderBottom: '2px solid rgba(0,0,0,0.2)' }}
              >
                Date
                <Box sx={{ fontSize: '0.65rem', fontWeight: 400, mt: 0.5, lineHeight: 1.4, display: 'flex', gap: 0.5 }}>
                  <Box component="span" sx={{ color: '#ef6c00' }}>Alloc</Box>
                  <Box component="span" sx={{ color: '#2e7d32' }}>Shipped</Box>
                  <Box component="span" sx={{ color: '#d32f2f' }}>Outstd</Box>
                </Box>
              </TableCell>
              {tables.map((t, tIdx) => (
                <TableCell
                  key={tIdx}
                  align="center"
                  colSpan={(t.columns?.length || 0) + 1}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    borderLeft: '4px solid rgba(0,0,0,0.6)',
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    backgroundColor: 'grey.50',
                  }}
                >
                  {t.name}
                </TableCell>
              ))}
            </TableRow>
            {/* Row 2: Individual column names + Total */}
            <TableRow>
              {tables.map((t, tIdx) => (
                <React.Fragment key={tIdx}>
                  {(t.columns || []).map((col, cIdx) => (
                    <TableCell
                      key={`${tIdx}-${cIdx}`}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        borderLeft: cIdx === 0 ? '4px solid rgba(0,0,0,0.6)' : undefined,
                        pl: cIdx === 0 ? 2 : undefined,
                        borderBottom: '2px solid rgba(0,0,0,0.2)',
                      }}
                    >
                      <Tooltip title={col.prefixes.join(', ')}>
                        <span>{col.name}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      borderBottom: '2px solid rgba(0,0,0,0.2)',
                    }}
                  >
                    Total
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => {
              if (row.type === 'overdue-header') {
                return (
                  <TableRow key={row.key} sx={{ '& td': { border: 0, p: 0 } }}>
                    <TableCell
                      colSpan={totalColumns}
                      sx={{
                        backgroundColor: '#d32f2f',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        py: '8px !important',
                        px: '16px !important',
                      }}
                    >
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }

              if (row.type === 'month-header') {
                return (
                  <TableRow key={row.key} sx={{ '& td': { border: 0, p: 0 } }}>
                    <TableCell
                      colSpan={totalColumns}
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        py: '8px !important',
                        px: '16px !important',
                        pt: row.key === tableRows.find(r => r.type === 'month-header')?.key ? '0 !important' : '20px !important',
                      }}
                    >
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }

              if (row.type === 'week-header') {
                return (
                  <TableRow key={row.key} sx={{ '& td': { border: 0, p: 0 } }}>
                    <TableCell
                      colSpan={totalColumns}
                      sx={{
                        backgroundColor: 'grey.200',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        py: '4px !important',
                        px: '16px !important',
                        color: 'text.secondary',
                      }}
                    >
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow
                  key={row.key}
                  sx={{
                    backgroundColor: row.isToday
                      ? 'rgba(25, 118, 210, 0.08)'
                      : row.isOverdue
                        ? 'rgba(211, 47, 47, 0.06)'
                        : 'inherit',
                    '&:hover': { backgroundColor: 'action.selected' },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: row.isToday ? 700 : 500,
                      color: row.isToday ? 'primary.main' : row.isOverdue ? '#d32f2f' : 'inherit',
                      whiteSpace: 'nowrap',
                      pl: 4,
                    }}
                  >
                    {row.isToday ? `Today - ${row.displayDate}` : row.displayDate}
                  </TableCell>
                  {tables.map((t, tIdx) => {
                    const td = tableData[tIdx];
                    if (!td) return null;
                    const total = td.daily_totals[row.dateStr] || 0;
                    const alertBg = row.isOverdue ? '#ef5350' : getTotalAlertColor(total, t.alert_thresholds);
                    return (
                      <React.Fragment key={tIdx}>
                        {(td.columns || []).map((col, cIdx) => (
                          <TableCell
                            key={`${tIdx}-${cIdx}`}
                            align="center"
                            sx={{
                              borderLeft: cIdx === 0 ? '4px solid rgba(0,0,0,0.6)' : undefined,
                              pl: cIdx === 0 ? 2 : undefined,
                              py: 0.5,
                              backgroundColor: row.isOverdue ? '#ef5350' : undefined,
                              color: row.isOverdue ? '#fff' : 'inherit',
                            }}
                          >
                            <CellValues
                              ordered={(valueMode ? col.value : col.totals)?.[row.dateStr] || 0}
                              allocated={(valueMode ? col.value_allocated : col.allocated)?.[row.dateStr] || 0}
                              shipped={(valueMode ? col.value_shipped : col.shipped)?.[row.dateStr] || 0}
                              outstanding={(valueMode ? col.value_outstanding : col.outstanding)?.[row.dateStr] || 0}
                              inheritColor={row.isOverdue}
                              hideZeros={hideZeros}
                              hideAllocations={hideAllocations}
                              currency={valueMode}
                              onClick={() => openDetails(row, t, t.columns?.[cIdx])}
                            />
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor: alertBg,
                            color: alertBg ? '#fff' : 'inherit',
                            py: 0.5,
                          }}
                        >
                          <CellValues
                            ordered={valueMode ? (td.daily_value?.[row.dateStr] || 0) : total}
                            allocated={(valueMode ? td.daily_value_allocated : td.daily_allocated)?.[row.dateStr] || 0}
                            shipped={(valueMode ? td.daily_value_shipped : td.daily_shipped)?.[row.dateStr] || 0}
                            outstanding={(valueMode ? td.daily_value_outstanding : td.daily_outstanding)?.[row.dateStr] || 0}
                            inheritColor={!!alertBg}
                            hideZeros={hideZeros}
                            hideAllocations={hideAllocations}
                            currency={valueMode}
                            emphasis
                            onClick={() => openDetails(row, t, null)}
                          />
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <CellDetailsDialog info={detailInfo} currency={valueMode} onClose={() => setDetailInfo(null)} />

      <TablesConfigDialog
        open={configDialogOpen}
        tables={editingTables}
        onTablesChange={setEditingTables}
        onSave={handleSaveTables}
        onClose={() => setConfigDialogOpen(false)}
      />
    </Container>
  );
};

export default CapacityPlanning;
